import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { dbHelpers } from '../lib/supabase';
import './PresenterUrlSubmission.css';

const PresenterUrlSubmission = ({ sessionId, sessionTitle }) => {
  const [formData, setFormData] = useState({
    presenter_name: '',
    presenter_email: '',
    url: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submittedUrls, setSubmittedUrls] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const loadSubmittedUrls = useCallback(async () => {
    try {
      const { data, error } = await dbHelpers.getApprovedPresenterUrls(sessionId);
      if (error) {
        console.error('Error loading presenter URLs:', error);
      } else {
        setSubmittedUrls(data || []);
      }
    } catch (err) {
      console.error('Error loading presenter URLs:', err);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSubmittedUrls();
  }, [sessionId, loadSubmittedUrls]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateUrl = (url) => {
    // Allow URLs without protocol by adding http:// if missing
    let testUrl = url.trim();
    if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
      testUrl = 'http://' + testUrl;
    }

    try {
      new URL(testUrl);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeUrl = (url) => {
    // Add http:// if no protocol is specified
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'http://' + normalizedUrl;
    }
    return normalizedUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    console.log('🚀 [PresenterUrlSubmission] Starting URL submission process');
    console.log('📝 [PresenterUrlSubmission] Form data:', formData);
    console.log('🎯 [PresenterUrlSubmission] Session ID:', sessionId);

    // Validation
    if (!formData.presenter_name.trim()) {
      console.log('❌ [PresenterUrlSubmission] Validation failed: Missing presenter name');
      setError('Presenter name is required');
      setLoading(false);
      return;
    }

    if (!formData.presenter_email.trim()) {
      console.log('❌ [PresenterUrlSubmission] Validation failed: Missing email');
      setError('Presenter email is required');
      setLoading(false);
      return;
    }

    if (!formData.url.trim()) {
      console.log('❌ [PresenterUrlSubmission] Validation failed: Missing URL');
      setError('URL is required');
      setLoading(false);
      return;
    }

    if (!validateUrl(formData.url)) {
      console.log('❌ [PresenterUrlSubmission] Validation failed: Invalid URL format:', formData.url);
      setError('Please enter a valid URL');
      setLoading(false);
      return;
    }

    try {
      const normalizedUrl = normalizeUrl(formData.url);
      const submissionData = {
        ...formData,
        url: normalizedUrl,
        session_id: sessionId
      };

      console.log('📤 [PresenterUrlSubmission] Submitting data:', submissionData);
      const { success: submitSuccess, error: submitError } = await dbHelpers.submitPresenterUrl(submissionData);
      console.log('📥 [PresenterUrlSubmission] Submission result:', { success: submitSuccess, error: submitError });

      if (submitSuccess) {
        console.log('✅ [PresenterUrlSubmission] URL submitted successfully!');
        setSuccess('URL submitted successfully! It will appear once approved by an admin.');
        setFormData({
          presenter_name: '',
          presenter_email: '',
          url: '',
          description: ''
        });
        setShowForm(false);
        // Reload approved URLs in case this was an update
        console.log('🔄 [PresenterUrlSubmission] Reloading submitted URLs...');
        await loadSubmittedUrls();
      } else {
        console.log('❌ [PresenterUrlSubmission] Submission failed:', submitError);
        setError(submitError?.message || 'Failed to submit URL');
      }
    } catch (err) {
      console.error('💥 [PresenterUrlSubmission] Exception during submission:', err);
      setError('An unexpected error occurred');
      console.error('Error submitting presenter URL:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="presenter-url-submission">
      <div className="presenter-urls-header">
        <h3>Presenter Resources</h3>
        {!showForm && (
          <button
            className="submit-url-btn"
            onClick={() => setShowForm(true)}
          >
            Submit Resource URL
          </button>
        )}
      </div>

      {/* Display approved URLs */}
      {submittedUrls.length > 0 && (
        <div className="approved-urls">
          <h4>Available Resources:</h4>
          <div className="urls-list">
            {submittedUrls.map((urlData) => (
              <div key={urlData.id} className="url-item">
                <div className="url-content">
                  <a
                    href={urlData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="url-link"
                  >
                    {urlData.url}
                  </a>
                  {urlData.description && (
                    <p className="url-description">{urlData.description}</p>
                  )}
                  <div className="url-meta">
                    <span className="presenter-name">By: {urlData.presenter_name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submission form */}
      {showForm && (
        <div className="url-submission-form">
          <div className="form-header">
            <h4>Submit a Resource URL for: {sessionTitle}</h4>
            <button
              className="close-form-btn"
              onClick={() => {
                setShowForm(false);
                setError('');
                setSuccess('');
              }}
            >
              ×
            </button>
          </div>

          {error && <div className="error-message">{typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error)}</div>}
          {success && <div className="success-message">{typeof success === 'object' ? (success.message || JSON.stringify(success)) : String(success)}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="presenter_name">Your Name *</label>
              <input
                type="text"
                id="presenter_name"
                name="presenter_name"
                value={formData.presenter_name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="presenter_email">Your Email *</label>
              <input
                type="email"
                id="presenter_email"
                name="presenter_email"
                value={formData.presenter_email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email address"
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">Resource URL *</label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                required
                placeholder="example.com/your-resource or https://example.com/your-resource"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of the resource"
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowForm(false);
                  setError('');
                  setSuccess('');
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit URL'}
              </button>
            </div>
          </form>

          <div className="submission-note">
            <p><strong>Note:</strong> Your submitted URL will be reviewed by an admin before it becomes visible to attendees.</p>
          </div>
        </div>
      )}
    </div>
  );
};

PresenterUrlSubmission.propTypes = {
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sessionTitle: PropTypes.string.isRequired
};

export default PresenterUrlSubmission;